import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  useTheme,
  CardActionArea,
} from '@mui/material';
import { Account } from '../../../types';
import { formatCurrency, calculateCreditPercentage, getStatusColor } from '../../../utils/helpers';

interface AccountCardProps {
  account: Account;
  onClick: (account: Account) => void;
}

const AccountCard: React.FC<AccountCardProps> = ({ account, onClick }) => {
  const theme = useTheme();
  const {
    name,
    balance,
    creditLimit,
    availableCredit,
    paymentTerm,
    accountStatus,
  } = account;

  // Calculate credit usage percentage
  const creditPercentage = calculateCreditPercentage(availableCredit, creditLimit);
  
  // Determine border color based on account status
  const borderColor = getStatusColor(accountStatus);
  
  // Determine progress bar color based on credit usage
  const getProgressColor = () => {
    if (creditPercentage < 25) return theme.palette.error.main;
    if (creditPercentage < 50) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `4px solid ${borderColor}`,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        },
      }}
    >
      <CardActionArea onClick={() => onClick(account)} sx={{ flexGrow: 1 }}>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" component="h2" noWrap sx={{ mb: 0.5, maxWidth: '70%' }}>
              {name}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Chip
                label={accountStatus}
                size="small"
                sx={{
                  backgroundColor: `${borderColor}20`,
                  color: borderColor,
                  fontWeight: 'bold',
                }}
              />
              {account.daysSinceLastContact !== undefined && account.daysSinceLastContact !== null ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  Last Contacted: {account.daysSinceLastContact === 0 ? 'Today' : 
                    account.daysSinceLastContact === 1 ? 'Yesterday' : 
                    `${account.daysSinceLastContact} days ago`}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                  Never Contacted
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Credit Limit: {account.creditLimit === 99999999 ? 'Unlimited' : formatCurrency(account.creditLimit)}
            </Typography>
            <Typography variant="h6" component="p">
              {formatCurrency(balance)}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Credit Limit
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(creditLimit)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(availableCredit)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={creditPercentage}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getProgressColor(),
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Payment Term: {paymentTerm} days
            </Typography>
          </Box>
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Xero ID: {account.contactID}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AccountCard;
